import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { productOptions } from '../../productOptions';
import { getMoneyDisplay, getShopCurrencyCode, parseFormattedMoney, syncMoneyDisplay } from '../../utils/currency';
import { RegExpMatcher, englishDataset, englishRecommendedTransformers } from 'obscenity';

/**
 * Addon selection
 *
 * @author Matt Haynes <Matthew.haynes@joipolloi.com>
 * @package Minifigs
 */

const frontTextFieldNames = [
    'input[name="properties[Text]"]',
    'input[name="properties[What text would you like?]"]',
    'input[name="properties[Would you like text on the front?]"]',
    'input[name="properties[Text for the front]"]',
    'textarea[name="properties[Text]"]',
    'textarea[name="properties[What text would you like?]"]',
];
const backTextFieldNames = [
    'input[name="properties[Text for the back]"]',
    'input[name="properties[What back text would you like?]"]',
    'textarea[name="properties[Text for the back]"]',
    'textarea[name="properties[What back text would you like?]"]',
];

const elementPropType = typeof Element !== 'undefined' ? PropTypes.instanceOf(Element) : PropTypes.any;

const acceptedFileTypes =
    'image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml,image/bmp,image/tiff,image/avif,image/heic,.jps,.pdf,.ai,.eps,.psd';

const imageOnlyFileTypes =
    'image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml,image/bmp,image/tiff,image/avif,image/heic';

const Addons = ({
    addons,
    productId,
    torsoText: torsoTextRedux = '',
    frontTextEventListenerExists = false,
    backTextEventListenerExists = false,
    setBackTextEventListenerExists,
    setFrontTextEventListenerExists,
    setTorsoBackText,
    setTorsoFrontText,
    addonsContainerRef,
    disabled,
}) => {
    const addonRef = useRef(null);
    const addonsContainer = useRef(null);
    const [hasLanguageWarning, setHasLanguageWarning] = useState(false);
    const currentFrontTextRef = useRef('');
    const currentBackTextRef = useRef('');
    const matcher = useMemo(
        () =>
            new RegExpMatcher({
                ...englishDataset.build(),
                ...englishRecommendedTransformers,
            }),
        []
    );

    const handleAddonsContainerRef = useCallback(
        (node) => {
            addonsContainer.current = node;

            if (typeof addonsContainerRef === 'function') {
                addonsContainerRef(node);
            } else if (addonsContainerRef && typeof addonsContainerRef === 'object') {
                addonsContainerRef.current = node;
            }
        },
        [addonsContainerRef]
    );

    useEffect(() => {
        if (!addonsContainer.current) return;

        // Blocks emojis, fancy Unicode fonts, mathematical alphanumerics, combining marks, etc.
        const sanitizeText = (str) => str.replace(/[^\u0020-\u007E\t\n\r]/g, '').replace(/\s+/g, ' ');

        const checkForObscenities = () => {
            const frontText = currentFrontTextRef.current;
            const backText = currentBackTextRef.current;
            const hasFrontObscenity = frontText && matcher.hasMatch(frontText);
            const hasBackObscenity = backText && matcher.hasMatch(backText);
            setHasLanguageWarning(hasFrontObscenity || hasBackObscenity);
        };

        const convertBoldPrices = () => {
            addonsContainer.current?.querySelectorAll('.bold_option_value_price').forEach((el) => {
                const gbpAmount = el.dataset.builderShopAmount === undefined
                    ? parseFormattedMoney(el.textContent)
                    : Number(el.dataset.builderShopAmount);
                if (!Number.isFinite(gbpAmount)) return;

                el.dataset.builderShopAmount = String(gbpAmount);
                const display = getMoneyDisplay(gbpAmount, getShopCurrencyCode());
                if (!display.allowCbb) {
                    el.dataset.cbbDisabled = 'true';
                } else {
                    delete el.dataset.cbbDisabled;
                }
                if (el.innerHTML !== display.html) el.innerHTML = display.html;
                syncMoneyDisplay(el, getShopCurrencyCode(), display.allowCbb);
            });
        };

        // File change handler for custom torso - prevents non-image uploads
        const handleFileChange = (e) => {
            const file = e.target.files?.[0];
            if (file && productId === '7841586053294') {
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                    e.target.value = '';
                }
            }
        };

        // Attach file handler only for custom torso product
        const attachFileHandler = (input) => {
            if (productId === '7841586053294') {
                input.addEventListener('change', handleFileChange);
            }
        };

        const handleNodeUpdate = () => {
            if (!backTextEventListenerExists) {
                for (const backTextFieldName of backTextFieldNames) {
                    const backTextField = addonsContainer.current.querySelector(backTextFieldName);
                    if (backTextField) {
                        setBackTextEventListenerExists(true);

                        backTextField.addEventListener('input', () => {
                            const strippedValue = sanitizeText(backTextField.value);
                            setTorsoBackText(strippedValue);
                            currentBackTextRef.current = strippedValue;
                            checkForObscenities();
                        });

                        backTextField.addEventListener('change', () => {
                            const strippedValue = sanitizeText(backTextField.value);
                            setTorsoBackText(strippedValue);
                            currentBackTextRef.current = strippedValue;
                            checkForObscenities();
                        });

                        break;
                    }
                }
            }

            if (!frontTextEventListenerExists) {
                for (const frontTextFieldName of frontTextFieldNames) {
                    const frontTextField = addonsContainer.current.querySelector(frontTextFieldName);
                    if (frontTextField) {
                        setFrontTextEventListenerExists(true);

                        frontTextField.addEventListener('input', () => {
                            const strippedValue = sanitizeText(frontTextField.value);
                            setTorsoFrontText(strippedValue);
                            currentFrontTextRef.current = strippedValue;
                            checkForObscenities();
                        });

                        frontTextField.addEventListener('change', () => {
                            console.log('Front text field change event');
                            const strippedValue = sanitizeText(frontTextField.value);
                            setTorsoFrontText(strippedValue);
                            currentFrontTextRef.current = strippedValue;
                            checkForObscenities();
                        });

                        break;
                    }
                }
            }

            // Attach text sanitization to all text inputs (blocks emojis + fancy Unicode fonts)
            const inputs = addonsContainer.current.querySelectorAll('textarea, input[type="text"]');
            inputs.forEach((input) => {
                if (!input.dataset.textSanitizerAttached) {
                    input.dataset.textSanitizerAttached = 'true';

                    input.addEventListener('paste', (e) => {
                        e.preventDefault();
                        const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                        const sanitized = sanitizeText(pastedText);

                        const start = input.selectionStart;
                        const end = input.selectionEnd;
                        input.value = input.value.substring(0, start) + sanitized + input.value.substring(end);

                        const newPos = start + sanitized.length;
                        input.setSelectionRange(newPos, newPos);

                        input.dispatchEvent(new Event('input', { bubbles: true }));
                    });

                    input.addEventListener('input', () => {
                        const cursorPos = input.selectionStart;
                        const originalLength = input.value.length;
                        const sanitized = sanitizeText(input.value);

                        if (sanitized !== input.value) {
                            const removedChars = originalLength - sanitized.length;
                            input.value = sanitized;
                            const newPos = Math.max(0, cursorPos - removedChars);
                            input.setSelectionRange(newPos, newPos);
                        }
                    });
                }
            });
        };

        const observer = new MutationObserver((mutationList) => {
            mutationList
                .filter((m) => m.type === 'childList')
                .forEach((m) => {
                    m.addedNodes.forEach((node) => {
                        if (node && node.nodeType === 1) {
                            const el = node;
                            if (
                                (typeof el.matches === 'function' && el.matches('textarea, input[type="text"]')) ||
                                (typeof el.querySelector === 'function' &&
                                    el.querySelector('textarea, input[type="text"]'))
                            ) {
                                handleNodeUpdate();
                            }
                            // Add accept attribute to file inputs to restrict to images and design files
                            if (typeof el.matches === 'function' && el.matches('input[type="file"]')) {
                                el.accept = productId === '7841586053294' ? imageOnlyFileTypes : acceptedFileTypes;
                                attachFileHandler(el);
                            } else if (typeof el.querySelector === 'function') {
                                const fileInputs = el.querySelectorAll('input[type="file"]');
                                fileInputs.forEach((input) => {
                                    input.accept =
                                        productId === '7841586053294' ? imageOnlyFileTypes : acceptedFileTypes;
                                    attachFileHandler(input);
                                });
                            }
                        }
                    });
                });
            convertBoldPrices();
        });

        observer.observe(addonsContainer.current, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style'] });

        handleNodeUpdate();

        // Set accept attribute on any existing file inputs
        const existingFileInputs = addonsContainer.current.querySelectorAll('input[type="file"]');
        existingFileInputs.forEach((input) => {
            input.accept = productId === '7841586053294' ? imageOnlyFileTypes : acceptedFileTypes;
            attachFileHandler(input);
        });

        convertBoldPrices();
        const conversionInterval = setInterval(convertBoldPrices, 500);

        return () => {
            observer.disconnect();
            clearInterval(conversionInterval);
        };
    }, [
        frontTextEventListenerExists,
        backTextEventListenerExists,
        torsoTextRedux,
        setBackTextEventListenerExists,
        setFrontTextEventListenerExists,
        setTorsoFrontText,
        setTorsoBackText,
        matcher,
        productId,
    ]);

    const addonsArr = addons?.option_product?.option_sets;
    let addonsRet = false;

    console.log({ productId });

    if (typeof addonsArr === 'undefined' || !addonsArr) {
        window.addonArr = false;
    } else {
        window.addonArr = addonsArr;
        productOptions();

        setTimeout(() => {
            const el = document.querySelector('.bold_option_value_price');
            if (el) {
                el.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }, 100); // tweak 50–300ms depending on Bold’s speed

        addonsRet = <div ref={addonRef} className="bold_options" data-product-id={`${productId}`} />;
    }

    return (
        <div className={cn('product-addons', { disabled })} ref={handleAddonsContainerRef}>
            {addonsArr && addonsRet && (
                <>
                    <h3>Customise your product</h3>
                    {addonsRet}
                </>
            )}
            {hasLanguageWarning && (
                <div className="language-warning" style={{ color: 'red' }}>
                    The text you have entered may contain inappropriate language. Please be aware that we hold the right
                    to alter or cancel any orders that are deemed offensive/inappropriate.
                </div>
            )}
        </div>
    );
};

Addons.propTypes = {
    addons: PropTypes.shape({
        option_product: PropTypes.shape({
            option_sets: PropTypes.array,
        }),
    }),
    productId: PropTypes.string.isRequired,
    addonsContainerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: elementPropType })]),
    torsoText: PropTypes.string,
    frontTextEventListenerExists: PropTypes.bool,
    backTextEventListenerExists: PropTypes.bool,
    setBackTextEventListenerExists: PropTypes.func.isRequired,
    setFrontTextEventListenerExists: PropTypes.func.isRequired,
    setTorsoBackText: PropTypes.func.isRequired,
    setTorsoFrontText: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

const mapStateToProps = (state) => {
    return {
        torsoText: state.builds[state.currentBuildIndex].torsoText,
    };
};

export default connect(mapStateToProps)(Addons);
